<template>
  <v-data-table :headers="headers" :items="tasks" :search="search" sort-by="calories" class="elevation-1">
    <template v-slot:top>
      <v-toolbar flat color="white">
        <v-toolbar-title>Tasks</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        ></v-text-field>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" max-width="500px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="mb-2" v-bind="attrs" v-on="on">Add Task</v-btn>
          </template>
          <v-card>
            <validation-observer v-slot="{ handleSubmit }">
              <form @submit.prevent="handleSubmit(save)">
                <v-card-title class="headline grey lighten-2" primary-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-row>
                      <v-col cols="6">
                        <validation-provider
                          :vid="uuid()"
                          name="Name"
                          :rules="`required|uniqueName:${existingTaskNames}`"
                          v-slot="{ errors }"
                        >
                          <v-text-field label="Name" v-model="editedTask.name"></v-text-field>
                          <span class="validation-message">
                            {{
                            errors[0]
                            }}
                          </span>
                        </validation-provider>
                      </v-col>
                      <v-col cols="6">
                        <validation-provider
                          :vid="uuid()"
                          name="Priority"
                          rules="required"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            label="Priority"
                            v-model.number="editedTask.priority"
                            type="number"
                          ></v-text-field>
                          <span class="validation-message">
                            {{
                            errors[0]
                            }}
                          </span>
                        </validation-provider>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="6">
                        <v-select
                          label="Triggering"
                          :items="triggerings"
                          v-model="editedTask.triggering"
                        ></v-select>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-if="editedTask.triggering === 'Cyclic'"
                          label="Interval"
                          v-model="editedTask.interval"
                        >
                          <v-icon slot="append" @click="configInterval">mdi-plus</v-icon>
                        </v-text-field>

                        <v-text-field
                          v-if="editedTask.triggering === 'Interupt'"
                          label="Single"
                          v-model="editedTask.single"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="blue darken-1" text @click="resetDialog">Cancel</v-btn>
                  <v-btn color="blue darken-1" text type="submit">Save</v-btn>
                </v-card-actions>
              </form>
            </validation-observer>
          </v-card>
        </v-dialog>

        <v-dialog v-model="intervalDialog" max-width="400">
          <v-card>
            <v-card-title class="headline grey lighten-2" primary-title>
              <span class="headline">Edit interval</span>
            </v-card-title>

            <v-card-text>
              <v-row>
                <v-col cols="6">
                  <v-text-field label="Days" v-model.number="interval.days" type="number"></v-text-field>
                </v-col>

                <v-col cols="6">
                  <v-text-field label="Hours" v-model.number="interval.hours" type="number"></v-text-field>
                </v-col>

                <v-col cols="6">
                  <v-text-field label="Minutes" v-model.number="interval.minutes" type="number"></v-text-field>
                </v-col>

                <v-col cols="6">
                  <v-text-field label="Seconds" v-model.number="interval.seconds" type="number"></v-text-field>
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    label="Milliseconds"
                    v-model.number="interval.milliseconds"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" text @click="saveInterval">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-toolbar>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-icon small class="mr-2" @click="editTask(item)">mdi-pencil</v-icon>
      <v-icon small @click="deleteTask(item)">mdi-delete</v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { find, map, filter } from "lodash";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import uuid from "src/mixins/uuid";

export default {
  name: "ResourceTasks",
  components: { ValidationObserver, ValidationProvider },
  props: ["resourceTempId"],
  mixins: [uuid],
  data: () => ({
    search: '',
    dialog: false,
    intervalDialog: false,
    headers: [
      {
        text: "Name",
        align: "start",
        sortable: false,
        value: "name",
      },
      { text: "Triggering", value: "triggering" },
      { text: "Single", value: "single" },
      { text: "Interval", value: "interval" },
      { text: "Priority", value: "priority" },
      { text: "Actions", value: "actions", sortable: false },
    ],
    editedTask: {},
    triggerings: ["Cyclic", "Interupt"],
    interval: {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    },
  }),
  computed: {
    formTitle() {
      return this.editedTask.tempId ? "Edit Task" : "New Task";
    },
    tasks() {
      const tasks = find(this.$store.state.configuration.resources, {
        tempId: this.resourceTempId,
      }).tasks;

      return map(tasks, (task) => ({
        ...task,
        triggering: isFinite(task.interval) ? "Cyclic" : "Interupt",
      }));
    },
    existingTaskNames() {
      let tasks = [];
      const resourceTasks = find(this.$store.state.configuration.resources, {
        tempId: this.resourceTempId,
      }).tasks

      if (this.editedTask.tempId) {
        tasks = filter(
          resourceTasks,
          (task) => task.tempId !== this.editedTask.tempId
        );
      } else {
        tasks = resourceTasks
      }

      return map(tasks, 'name').join(';')
    },
  },
  watch: {
    dialog(newValue) {
      if (!newValue) {
        this.resetDialog();
      }
    },
  },
  methods: {
    editTask(task) {
      this.editedTask = Object.assign({}, task);
      this.dialog = true;
    },
    configInterval() {
      this.intervalDialog = true;
      if (/^T#\d+d\d+h\d+m\d+s\d+ms$/.test(this.editedTask.interval)) {
        const [
          _,
          days,
          hours,
          minutes,
          seconds,
          milliseconds,
        ] = this.editedTask.interval.match(
          /^T#(\d+)d(\d+)h(\d+)m(\d+)s(\d+)ms$/
        );
        this.interval = {
          days,
          hours,
          minutes,
          seconds,
          milliseconds,
        };
      }
    },
    saveInterval() {
      this.intervalDialog = false;
      this.editedTask.interval = `T#${this.interval.days}d${this.interval.hours}h${this.interval.minutes}m${this.interval.seconds}s${this.interval.milliseconds}ms`;
      this.interval = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      };
    },

    async deleteTask(task) {
      if (await this.$confirm("Are you sure you want to delete this task?")) {
        this.$store.dispatch("deleteResourceTask", {
          resourceTempId: this.resourceTempId,
          taskTempId: task.tempId,
        });
      }
    },
    resetDialog() {
      this.dialog = false;
      this.editedTask = {};
    },
    save() {
      if (this.editedTask.triggering === "Cyclic") {
        this.editedTask.single = undefined;
      } else {
        this.editedTask.interval = undefined;
      }

      if (this.editedTask.tempId) {
        this.$store.dispatch("updateResourceTask", {
          resourceTempId: this.resourceTempId,
          task: this.editedTask,
        });
      } else {
        this.$store.dispatch("addResourceTask", {
          resourceTempId: this.resourceTempId,
          task: {
            tempId: this.uuid(),
            ...this.editedTask,
            pouInstances: [],
          },
        });
      }

      this.resetDialog();
    },
  },
};
</script>
